/**
 * "Spliiit lives in the app now" (Sept 2026): what a normal web browser shows
 * instead of the web app. The iOS app, the Android app (TWA) and installed
 * home-screen apps never see this — see isAppContext() in App.tsx.
 *
 * Signed-in web users keep their account: everything is still there once they
 * sign in to the app with the same login. Admins are exempt (admin dashboard).
 */
import { useAuth } from "@/lib/auth";
import { copyInviteForInstall } from "@/lib/invite-handoff";

export const APP_STORE_URL = "https://apps.apple.com/app/id6761338254";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=ca.klarityit.spliiit";
// Both apps are registered for this host (iOS universal links + Android app links).
const APP_HOST = "spliiit.klarityit.ca";

/** A link that opens the installed app (onto the invite, if there is one). */
function openInAppUrl(os: "ios" | "android" | "other", inviteCode?: string): string | null {
  const play = inviteCode ? `${PLAY_STORE_URL}&referrer=${encodeURIComponent(`join=${inviteCode}`)}` : PLAY_STORE_URL;
  const q = inviteCode ? `?join=${encodeURIComponent(inviteCode)}` : "";
  if (os === "ios") return `https://${APP_HOST}/${q}`;
  if (os === "android") {
    return `intent://${APP_HOST}/${q}#Intent;scheme=https;package=ca.klarityit.spliiit;S.browser_fallback_url=${encodeURIComponent(play)};end`;
  }
  return null;
}

const APPLE = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-[22px] h-[22px]">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);
const PLAY = (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="w-[20px] h-[20px]">
    <path d="M3.6 2.3 13.5 12l-9.9 9.7c-.4-.2-.6-.6-.6-1.1V3.4c0-.5.2-.9.6-1.1z" fill="#00D7FE" />
    <path d="m16.8 8.7-3.3 3.3L3.6 2.3c.2-.1.5-.1.8.1l12.4 6.3z" fill="#00F076" />
    <path d="m16.8 15.3-12.4 6.3c-.3.2-.6.2-.8.1l9.9-9.7 3.3 3.3z" fill="#FF3A44" />
    <path d="M20.4 12c0 .5-.3 1-.8 1.2l-2.8 2.1-3.3-3.3 3.3-3.3 2.8 2.1c.5.2.8.7.8 1.2z" fill="#FFD109" />
  </svg>
);

function Logo() {
  return (
    <div className="w-[88px] h-[88px] rounded-[26px] bg-[hsl(38_18%_97%)] flex items-center justify-center shadow-[0_1px_1px_rgba(20,18,16,.08),0_10px_24px_-10px_rgba(20,18,16,.35)]">
      <svg viewBox="0 0 32 32" fill="none" aria-hidden="true" className="w-[52px] h-[52px] text-[hsl(30_6%_15%)]">
        {[10, 16, 22].map((x) => (
          <g key={x}>
            <circle cx={x} cy={8.8} r={1.5} fill="currentColor" />
            <path d={`M${x} 13.6V23.8`} stroke="currentColor" strokeWidth={3} strokeLinecap="round" />
          </g>
        ))}
      </svg>
    </div>
  );
}

// Your phone's store is the dark button; the other one sits underneath, light.
const DARK = "bg-[hsl(30_6%_15%)] text-[hsl(36_28%_92%)]";
const LIGHT = "bg-[hsl(38_18%_97%)] text-[hsl(30_6%_15%)] border border-[hsl(36_12%_82%)]";

function detectOS(): "ios" | "android" | "other" {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

export default function AppOnly({ inviteGroup, inviteCode }: { inviteGroup?: string; inviteCode?: string }) {
  const { user, logout } = useAuth();
  const os = detectOS();
  // With an invite: the tap copies the invite (the app offers "Paste invite" on
  // first open) and the Play link carries it as the install referrer.
  const onStoreTap = () => { if (inviteCode) copyInviteForInstall(inviteCode); };
  const playUrl = inviteCode ? `${PLAY_STORE_URL}&referrer=${encodeURIComponent(`join=${inviteCode}`)}` : PLAY_STORE_URL;
  const apple = (
    <a key="apple" href={APP_STORE_URL} target="_blank" rel="noopener" onClick={onStoreTap}
       className={`h-[54px] rounded-full flex items-center justify-center gap-2.5 text-[16px] font-medium ${os !== "android" ? DARK : LIGHT}`}
       data-testid="app-only-appstore">
      {APPLE}Download on the App Store
    </a>
  );
  const play = (
    <a key="play" href={playUrl} target="_blank" rel="noopener" onClick={onStoreTap}
       className={`h-[54px] rounded-full flex items-center justify-center gap-2.5 text-[16px] font-medium ${os === "android" ? DARK : LIGHT}`}
       data-testid="app-only-play">
      {PLAY}Get it on Google Play
    </a>
  );
  const buttons = os === "android" ? [play, apple] : [apple, play];
  const openUrl = openInAppUrl(os, inviteCode);

  const headline = inviteGroup ? <>You're invited to <em className="not-italic text-[hsl(18_42%_50%)]">{inviteGroup}</em>.</> : "Spliiit lives in the app now.";
  const sub = inviteGroup
    ? "Download Spliiit and sign up. When the app asks \u201cInvited by a friend?\u201d, tap Paste and you're in."
    : user
      ? "Everything you've added is still there. Download the app and sign in with the same account."
      : "Split bills by saying them. Download the app to get started.";

  return (
    <div className="min-h-[100dvh] bg-[hsl(36_28%_87%)] text-[hsl(30_6%_15%)] flex flex-col px-6 pt-[max(56px,env(safe-area-inset-top))] pb-[max(28px,env(safe-area-inset-bottom))]" data-testid="app-only">
      <div className="flex-1 flex flex-col items-center justify-center text-center max-w-[420px] w-full mx-auto">
        <Logo />
        <h1 className="mt-8 text-[40px] leading-[1.05] tracking-[-0.01em] [text-wrap:balance]" style={{ fontFamily: "var(--font-serif)" }}>{headline}</h1>
        <p className="mt-4 text-[16px] leading-[1.5] text-[hsl(30_4%_36%)] [text-wrap:pretty]">{sub}</p>
      </div>
      <div className="w-full max-w-[420px] mx-auto grid gap-3">
        {buttons}
        {openUrl && (
          <p className="mt-2 text-center text-[15px] text-[hsl(30_4%_36%)]">
            Already have Spliiit?{" "}
            <a href={openUrl} className="font-semibold text-[hsl(18_42%_50%)] underline underline-offset-2" data-testid="app-only-open">
              {inviteCode ? "Open this invite in Spliiit" : "Open Spliiit"}
            </a>
          </p>
        )}
        {user && (
          <p className="mt-3 text-center text-[13.5px] text-[hsl(30_3%_48%)]">
            Signed in as {user.email} · <button type="button" onClick={() => void logout()} className="underline underline-offset-2 text-[hsl(30_6%_15%)]" data-testid="app-only-signout">Sign out</button>
          </p>
        )}
      </div>
    </div>
  );
}
